import { type FormEvent, type ReactNode, useId, useState } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/admin/coaching/coaching-dashboard/shared/tooltip'
import { ESTIMATE_TUNING } from '@/lib/coaching/estimate-minutes'
import type { CurriculumItemType } from '../_lib/filter-engine'
import { EstimateStepper } from '../shared/estimate-stepper'

export type CustomCardType = Exclude<CurriculumItemType, 'interview'>

export interface CustomCardDraft {
  title: string
  type: CustomCardType
  link: string
  /** Minutes, stepped in 5s like the staging tray's control. */
  estimatedMinutes: number
  /** Free text the client sees under "Details" in their task dialog. */
  details: string
}

export interface CustomCardFormProps {
  targetLabel: string
  onAdd: (draft: CustomCardDraft) => void
  onClose: () => void
}

const CUSTOM_TYPES: readonly {
  type: CustomCardType
  label: string
}[] = [
  { type: 'coding', label: 'Coding' },
  { type: 'sd', label: 'Sys design' },
  { type: 'behavioral', label: 'Behavioral' },
  { type: 'other', label: 'Other' },
]

// The step size, floor, and default come from ESTIMATE_TUNING via the shared
// stepper, so a calibration pass moves this form with everything else — in
// particular the default, which must stay equal to the estimate engine's
// fallback. If it didn't, a coach who accepted the form's default would be
// writing an explicit override that renders amber and pins the card below the
// system default, having touched nothing.
//
// The ceiling is the one knob this form does NOT inherit. ESTIMATE_TUNING's
// 90m bounds a *derived per-problem* estimate; a custom card is arbitrary
// coach-authored work, and the <select> this replaced offered up to 3h. The
// server puts no upper bound on the column (`setEstimateInput` is a
// nonnegative int), so keeping 3h here preserves the capability instead of
// silently retiring it.
export const CUSTOM_CARD_ESTIMATE_MAX = 180
const INVALID_LINK_MESSAGE = 'Enter a full URL, such as https://example.com.'

export function isAbsoluteUrl(value: string): boolean {
  try {
    // http/https only — the link is rendered as an <a href>, so schemes like
    // javascript:/data: must not validate (LG-173).
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

export function CustomCardForm(props: CustomCardFormProps): ReactNode {
  const { targetLabel, onAdd, onClose } = props
  const [type, setType] = useState<CustomCardType>('coding')
  const [title, setTitle] = useState('')
  const [link, setLink] = useState('')
  const [linkError, setLinkError] = useState<string | null>(null)
  // Explicit <number>: ESTIMATE_TUNING is `as const`, so the initial value
  // would otherwise narrow the state to the literal 30.
  const [estimatedMinutes, setEstimatedMinutes] = useState<number>(
    ESTIMATE_TUNING.defaultEstimateMinutes,
  )
  const [details, setDetails] = useState('')
  const linkErrorId = useId()

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    const normalizedLink = link.trim()
    if (normalizedLink && !isAbsoluteUrl(normalizedLink)) {
      setLinkError(INVALID_LINK_MESSAGE)
      return
    }
    setLinkError(null)
    onAdd({
      title,
      type,
      link: normalizedLink,
      estimatedMinutes,
      details: details.trim(),
    })
  }

  return (
    <form
      aria-labelledby="custom-card-form-heading"
      onSubmit={submit}
      className="mb-2.5 rounded-xl border border-(--cd-border-2) bg-(--cd-card) px-3.5 py-3"
    >
      <div className="mb-2 flex items-center gap-2">
        <h3
          id="custom-card-form-heading"
          className="text-[11px] font-bold tracking-[0.07em] text-(--cd-text-muted)"
        >
          CUSTOM CARD
        </h3>
        <span className="text-[10.5px] text-(--cd-text-faint)">
          → {targetLabel}
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Close custom card form"
              onClick={onClose}
              className="ml-auto text-[11px] text-(--cd-text-faint) hover:text-(--cd-text)"
            >
              ✕
            </button>
          </TooltipTrigger>
          <TooltipContent>Close custom card form</TooltipContent>
        </Tooltip>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div
          role="group"
          aria-label="Custom card type"
          className="flex gap-0.5 rounded-full border border-(--cd-border) bg-(--cd-bg) p-0.5"
        >
          {CUSTOM_TYPES.map((option) => {
            const selected = type === option.type
            return (
              <button
                key={option.type}
                type="button"
                aria-pressed={selected}
                onClick={() => setType(option.type)}
                className="rounded-full px-2.5 py-0.5 text-[10px] font-bold whitespace-nowrap"
                style={{
                  background: selected ? 'var(--cd-tab-active)' : 'transparent',
                  color: selected ? 'var(--cd-text)' : 'var(--cd-text-muted)',
                }}
              >
                {option.label}
              </button>
            )
          })}
        </div>

        <input
          type="text"
          aria-label="Custom card title"
          value={title}
          onChange={(event) => setTitle(event.currentTarget.value)}
          placeholder="Task title — e.g. Watch: B-Trees explained (YouTube)"
          className="min-w-[220px] flex-1 rounded-lg border border-(--cd-border) bg-(--cd-bg) px-2.5 py-1.5 text-xs text-(--cd-text) outline-none focus:border-(--cd-text-faint)"
        />
        <input
          type="text"
          aria-label="Custom card link"
          aria-describedby={linkError ? linkErrorId : undefined}
          aria-invalid={linkError ? true : undefined}
          value={link}
          onChange={(event) => {
            setLink(event.currentTarget.value)
            setLinkError(null)
          }}
          placeholder="Link (optional)"
          className="w-[230px] rounded-lg border border-(--cd-border) bg-(--cd-bg) px-2.5 py-1.5 text-xs text-(--cd-text) outline-none focus:border-(--cd-text-faint)"
        />
        {linkError ? (
          <p
            className="w-full text-[10.5px] text-(--cd-red)"
            id={linkErrorId}
            role="alert"
          >
            {linkError}
          </p>
        ) : null}
        <EstimateStepper
          label="Estimated minutes"
          value={estimatedMinutes}
          onChange={setEstimatedMinutes}
          maxMinutes={CUSTOM_CARD_ESTIMATE_MAX}
        />
        {/* Flows straight through `CoachingAssignment.instructions` to the
            client's task dialog, which has always rendered it under a
            "Details" heading — every admin call site simply hardcoded null.
            It matters most for custom cards, whose title is all the client
            would otherwise get.

            Ordered before the submit button on purpose: it is `w-full`, so it
            takes its own row in this wrap container, and emitting it after
            "Add card" would put the primary action above the last field and
            let a coach tab (or Enter-submit) straight past it. */}
        <textarea
          aria-label="Custom card details"
          value={details}
          onChange={(event) => setDetails(event.currentTarget.value)}
          rows={2}
          placeholder="Details (optional) — what the client should do, and why"
          className="w-full resize-y rounded-lg border border-(--cd-border) bg-(--cd-bg) px-2.5 py-1.5 text-xs text-(--cd-text) outline-none focus:border-(--cd-text-faint)"
        />
        <button
          type="submit"
          className="rounded-lg bg-(--cd-green) px-4 py-1.5 text-xs font-bold text-(--cd-bg)"
        >
          Add card
        </button>
      </div>
    </form>
  )
}
